/**
 * useOnboarding Hook
 * Manages admin panel guided tours using driver.js.
 * Tracks completed tours per user in localStorage.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { driver, type DriveStep, type Config } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/useAuthStore';

// ─── Storage helpers ───────────────────────────────────────────
const STORAGE_KEY = 'yim-onboarding';

interface OnboardingState {
  [userId: string]: {
    completedTours: string[];
    /** ISO date of last dismissal – cooldown so it doesn't nag */
    dismissedAt?: string;
  };
}

function getState(): OnboardingState {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function setState(state: OnboardingState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function markTourCompleted(userId: string, tourId: string) {
  const state = getState();
  if (!state[userId]) state[userId] = { completedTours: [] };
  if (!state[userId].completedTours.includes(tourId)) {
    state[userId].completedTours.push(tourId);
  }
  setState(state);
}

function isTourCompleted(userId: string, tourId: string): boolean {
  const state = getState();
  return state[userId]?.completedTours?.includes(tourId) ?? false;
}

function resetUserTours(userId: string) {
  const state = getState();
  if (state[userId]) {
    state[userId].completedTours = [];
    setState(state);
  }
}

// ─── Tour definitions ──────────────────────────────────────────

export type TourId = 'dashboard';

/**
 * Build tour steps with translated text.
 * Elements are targeted via `data-tour="..."` attributes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getDashboardSteps(t: any): DriveStep[] {
  return [
    {
      element: '[data-tour="sidebar"]',
      popover: {
        title: t('onboarding.dashboard.sidebar.title', 'Sidebar Navigation'),
        description: t(
          'onboarding.dashboard.sidebar.desc',
          'Use the sidebar to navigate between all admin sections: members, events, blog, donations, and more.',
        ),
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="topbar-welcome"]',
      popover: {
        title: t('onboarding.dashboard.topbar.title', 'Top Bar'),
        description: t(
          'onboarding.dashboard.topbar.desc',
          'Here you can see your name, switch language, access notifications, and visit the public website.',
        ),
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="notifications"]',
      popover: {
        title: t('onboarding.dashboard.notifications.title', 'Notifications'),
        description: t(
          'onboarding.dashboard.notifications.desc',
          'The bell shows new activity: new members, donations, messages. Click to see details.',
        ),
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: '[data-tour="main-content"]',
      popover: {
        title: t('onboarding.dashboard.content.title', 'Main Content'),
        description: t(
          'onboarding.dashboard.content.desc',
          'This area shows the current page content. The dashboard gives you an overview of key metrics.',
        ),
        side: 'left',
        align: 'start',
      },
    },
    {
      element: '[data-tour="sidebar-settings"]',
      popover: {
        title: t('onboarding.dashboard.settings.title', 'Account & Settings'),
        description: t(
          'onboarding.dashboard.settings.desc',
          'Manage your profile, password, 2FA security, and system settings here.',
        ),
        side: 'right',
        align: 'end',
      },
    },
    {
      element: '[data-tour="sidebar-user"]',
      popover: {
        title: t('onboarding.dashboard.user.title', 'Your Account'),
        description: t(
          'onboarding.dashboard.user.desc',
          'Your profile info is shown here. Click "Logout" when you\'re done.',
        ),
        side: 'right',
        align: 'end',
      },
    },
  ];
}

// ─── Hook ──────────────────────────────────────────────────────

interface UseOnboardingReturn {
  /** Start a specific tour */
  startTour: (tourId: TourId) => void;
  /** Check if the tour was already completed */
  isCompleted: (tourId: TourId) => boolean;
  /** Reset all tours for current user (re-enables auto-start) */
  resetTours: () => void;
  /** Whether a tour is currently active */
  isActive: boolean;
}

export function useOnboarding(): UseOnboardingReturn {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.id);
  const [isActive, setIsActive] = useState(false);
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
    };
  }, []);

  const startTour = useCallback(
    (tourId: TourId) => {
      if (!userId) return;

      let steps: DriveStep[];
      switch (tourId) {
        case 'dashboard':
          steps = getDashboardSteps(t);
          break;
        default:
          return;
      }

      // Filter out steps whose target element doesn't exist in the DOM
      const validSteps = steps.filter((step) => {
        if (!step.element) return true; // non-element steps always valid
        return document.querySelector(step.element as string) !== null;
      });

      if (validSteps.length === 0) return;

      const config: Config = {
        showProgress: true,
        animate: true,
        allowClose: true,
        overlayColor: 'rgba(15, 23, 42, 0.65)',
        stagePadding: 8,
        stageRadius: 12,
        popoverClass: 'yim-tour-popover',
        nextBtnText: t('onboarding.next', 'Next') + ' →',
        prevBtnText: '← ' + t('onboarding.prev', 'Back'),
        doneBtnText: t('onboarding.done', 'Got it!'),
        progressText: '{{current}} / {{total}}',
        steps: validSteps,
        onDestroyed: () => {
          markTourCompleted(userId, tourId);
          setIsActive(false);
        },
      };

      driverRef.current?.destroy();
      const d = driver(config);
      driverRef.current = d;
      setIsActive(true);
      d.drive();
    },
    [userId, t],
  );

  const isCompleted = useCallback(
    (tourId: TourId): boolean => {
      if (!userId) return true;
      return isTourCompleted(userId, tourId);
    },
    [userId],
  );

  const resetTours = useCallback(() => {
    if (userId) resetUserTours(userId);
  }, [userId]);

  return { startTour, isCompleted, resetTours, isActive };
}

export default useOnboarding;
